import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import Breadcrumb from "../components/common/Breadcrumb";
import Banner from '../components/workflow/Banner';
import WorkflowReportMenu from '../components/workflow/WorkflowReportMenu';
import Select from 'react-select';
import DetailsPopup from '../components/workflow/DetailsPopup';
import { MenuProvider } from '../components/workflow/MenuContext';
import {
  getWorkkflowCombinations,
  getActualPredictedForecastResults,
  getTopOneModelResults,
  getForecastResults,
  getTopThreeModelResults
} from '../services/ApiManageWorkflow';
import { upsertRecord, deleteRecord } from "../services/IndexedDBUtil";
import { DATABASE_NAME, STORE_NAME } from '../services/constants';
import { displayMessage } from '../Utils/helper';

function WorkflowDetails() {
  const location = useLocation();
  const { state } = location;

  // read workflow_id from URL query param if present
  const searchParams = new URLSearchParams(location.search);
  const workflowIdFromQuery = searchParams.get("workflow_id");

  // fallback to workflow from state (old navigation)
  const workflowFromState = state?.workflow;
  const workflow_id = workflowIdFromQuery || workflowFromState?.workflow_id;

  const [showWFDetailsModal, setShowWFDetailsModal] = useState(false);
  const [combinations, setCombinations] = useState([]);
  const [selectedCombination, setSelectedCombination] = useState({ product: null, location: null });
  const [topThreeData, setTopThreeData] = useState(null); // 👈 for cards

  useEffect(() => {
    const init = async () => {
      if (!workflow_id) {
        displayMessage("danger", "Workflow ID is missing. Please open this page from the workflow list.");
        return;
      }

      try {
        const [
          combinationsRes,
          data1,
          data2,
          data3,
          data4
        ] = await Promise.all([
          getWorkkflowCombinations(workflow_id),
          getActualPredictedForecastResults(workflow_id),
          getTopOneModelResults(workflow_id),
          getForecastResults(workflow_id),
          getTopThreeModelResults(workflow_id)
        ]);

        setCombinations(combinationsRes);
        setTopThreeData(data4); // 👈 pass to menu

        // Clear old cached data
        await deleteRecord(DATABASE_NAME, STORE_NAME, "WFActualPredictedData");
        await deleteRecord(DATABASE_NAME, STORE_NAME, "WFModelResult");
        await deleteRecord(DATABASE_NAME, STORE_NAME, "WFForecastResult");
        await deleteRecord(DATABASE_NAME, STORE_NAME, "WFTOPThreeModelResult");

        // Save new data in IndexedDB (if other parts still use it)
        await upsertRecord(DATABASE_NAME, STORE_NAME, {
          id: "WFActualPredictedData",
          data: data1,
        });
        await upsertRecord(DATABASE_NAME, STORE_NAME, {
          id: "WFModelResult",
          data: data2,
        });
        await upsertRecord(DATABASE_NAME, STORE_NAME, {
          id: "WFForecastResult",
          data: data3,
        });
        await upsertRecord(DATABASE_NAME, STORE_NAME, {
          id: "WFTOPThreeModelResult",
          data: data4,
        });

        console.log("WorkflowDetails: data fetched successfully");
      } catch (err) {
        await deleteRecord(DATABASE_NAME, STORE_NAME, "WFActualPredictedData");
        await deleteRecord(DATABASE_NAME, STORE_NAME, "WFModelResult");
        await deleteRecord(DATABASE_NAME, STORE_NAME, "WFForecastResult");
        await deleteRecord(DATABASE_NAME, STORE_NAME, "WFTOPThreeModelResult");
        console.error(`Error while fetching data : ${err}`);
        displayMessage("danger", "Error while loading workflow details");
      }
    };

    init();
  }, [workflow_id]);

  if (!workflow_id) {
    // hard guard
    return null;
  }

  if (!combinations) {
    return false;
  }

  function getFormattedPath(obj) {
    let levels = ["level1", "level2", "level3", "level4", "level5"];
    let path = [];

    for (let key of levels) {
      if (!obj[key]) break;  // Stop if value is empty or null
      path.push(obj[key]);
    }

    return path.join("/");
  }

  const options = combinations.map(ob => ({
    label: `${ob.sku_name} - ${getFormattedPath(ob)}`,
    value: {
      sku_name: ob.sku_name,
      location: getFormattedPath(ob),
      sku_id: ob.sku_id
    }
  }));

  const formatOptionLabel = (data, { context }) => {
    return context === "menu" ? (
      <div>
        <span>{data.label}</span>
      </div>
    ) : (
      <>{data.label}</>
    );
  };

  const combinationSelectChangeHandler = async (selected) => {
    try {
      console.log(selected);
      let obj = {
        product: selected.value.sku_name,
        location: selected.value.location.split('/').pop()
      };
      setSelectedCombination(obj);
    } catch (err) {
      console.error(err);
      displayMessage("danger", "Error", "Error occured while fetching details");
    }
  };

  const customStyles = {
    menu: (provided) => ({
      ...provided,
      zIndex: 100,
      boxShadow: '0 12px 24px rgba(72,187,120,0.15)',
      border: '1px solid rgba(72,187,120,0.08)',
      borderRadius: '16px',
      overflow: 'hidden',
      padding: '8px',
      backgroundColor: '#ffffff'
    }),
    control: (provided) => ({
      ...provided,
      minHeight: '56px',
      height: '56px',
      borderRadius: '14px',
      fontSize: '1.2rem',
      border: '2px solid #48bb78',
      background: 'linear-gradient(145deg, rgba(255,255,255,0.95) 0%, #f8faff 100%)',
      boxShadow: '0 4px 16px rgba(26,35,126,0.08)',
      transition: 'all 0.3s ease',
      '&:hover': {
        border: '2px solid #38a169',
        boxShadow: '0 8px 24px rgba(26,35,126,0.12)',
        background: 'linear-gradient(145deg, #ffffff 0%, #f8faff 100%)'
      }
    }),
    singleValue: (provided) => ({
      ...provided,
      fontSize: '1.2rem',
      color: '#2f855a',
      fontWeight: 600,
      letterSpacing: '0.01em'
    }),
    option: (provided, state) => ({
      ...provided,
      fontSize: '1.2rem',
      padding: '12px 16px',
      backgroundColor: state.isSelected ? '#48bb78' : state.isFocused ? '#f0fff4' : '#ffffff',
      color: state.isSelected ? '#ffffff' : '#2f855a',
      cursor: 'pointer',
      fontWeight: 600,
      transition: 'all 0.2s ease',
      ':active': {
        backgroundColor: '#38a169',
        color: '#ffffff'
      },
      ':hover': {
        backgroundColor: state.isSelected ? '#48bb78' : '#f0fff4'
      }
    }),
    placeholder: (provided) => ({
      ...provided,
      color: 'rgba(72,187,120,0.7)',
      fontSize: '1.2rem',
      fontWeight: 500
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: '#48bb78',
      ':hover': {
        color: '#38a169'
      }
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      backgroundColor: 'rgba(72,187,120,0.2)'
    })
  };

  return (
    <>
      <Breadcrumb
        List={[
          { path: "/Dashboard", name: "Dashboard" },
          { path: "/ManageWorkflow", name: "Workflow" },
          {
            path: "#",
            name: workflowFromState?.workflow_name || `Workflow #${workflow_id}`
          }
        ]}
      />
      <div className="manage-workflow-box" style={{ margin: 0, padding: 0, maxWidth: "100%" }}>
        <MenuProvider>
          <WorkflowReportMenu
            key={workflow_id}  // force remount when workflow changes
            Product={selectedCombination.product}
            Location={selectedCombination.location}
            Workflow={workflowFromState || { workflow_id }}
            topThreeData={topThreeData}  // 👈 pass data
          >
            <div
              className="workflow-header"
              style={{
                marginTop: "20px",
                marginBottom: "20px",
                padding: "0 20px"
              }}
            >
              <div
                className="row"
                style={{
                  alignItems: "center",
                  marginBottom: "0px"
                }}
              >
                <div className="col-md-9 col-sm-12 col-xs-12">
                  <div
                    className="form-group1"
                    style={{
                      background: "#fbfcf7ff",
                      padding: "32px 36px",
                      borderRadius: "20px",
                      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
                      border: "2px solid #032B4E",
                      backdropFilter: "blur(8px)",
                      WebkitBackdropFilter: "blur(8px)"
                    }}
                  >
                    <label
                      style={{
                        fontFamily: "'Poppins', 'Inter', Arial, sans-serif",
                        fontSize: "20px",
                        fontWeight: 600,
                        color: "#0F1116",
                        marginBottom: "16px",
                        display: "block",
                        letterSpacing: "0.01em"
                      }}
                    >
                      Select Combination
                    </label>
                    <Select
                      closeMenuOnSelect={true}
                      onChange={combinationSelectChangeHandler}
                      formatOptionLabel={formatOptionLabel}
                      isSearchable
                      options={options}
                      styles={{
                        ...customStyles,
                        control: (provided) => ({
                          ...provided,
                          minHeight: "56px",
                          height: "56px",
                          borderRadius: "14px",
                          fontFamily: "'Inter', sans-serif",
                          fontSize: "14px",
                          fontWeight: 400,
                          border: "2px solid #032B4E",
                          boxShadow: "0 2px 8px rgba(3,43,78,0.06)",
                          backgroundColor: "rgba(255,255,255,0.9)",
                          backdropFilter: "blur(8px)",
                          WebkitBackdropFilter: "blur(8px)",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            border: "2px solid #032B4E",
                            boxShadow: "0 4px 12px rgba(3,43,78,0.1)",
                            backgroundColor: "rgba(255,255,255,0.95)"
                          }
                        }),
                        option: (provided, state) => ({
                          ...provided,
                          fontFamily: "'Inter', sans-serif",
                          fontSize: "14px",
                          padding: "12px 16px",
                          backgroundColor: state.isSelected
                            ? "#032B4E"
                            : state.isFocused
                            ? "#f8faff"
                            : "#ffffff",
                          color: state.isSelected ? "#ffffff" : "#0F1116",
                          fontWeight: 400
                        }),
                        singleValue: (provided) => ({
                          ...provided,
                          fontFamily: "'Inter', sans-serif",
                          fontSize: "14px",
                          color: "#0F1116",
                          fontWeight: 600
                        })
                      }}
                    />
                  </div>
                </div>
                <div className="col-md-3 col-sm-12 col-xs-12">
                  <button
                    type="button"
                    onClick={() => {
                      setShowWFDetailsModal(true);
                    }}
                    style={{
                      width: "100%",
                      padding: "16px 28px",
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#ffffff",
                      background: "#b8842f",
                      border: "none",
                      borderRadius: "14px",
                      boxShadow: "0 6px 16px rgba(184, 132, 47, 0.25)",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                      textTransform: "uppercase",
                      letterSpacing: "0.03em",
                      position: "relative",
                      overflow: "hidden",
                      transform: "translateY(0)"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#a67d2e";
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow = "0 8px 20px rgba(184, 132, 47, 0.35)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#b8842f";
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 6px 16px rgba(184, 132, 47, 0.25)";
                    }}
                    className="btn btn-primary btn-wf-detail"
                  >
                    Workflow Details
                  </button>
                </div>
              </div>
            </div>
          </WorkflowReportMenu>
        </MenuProvider>
      </div>
      {showWFDetailsModal &&
        createPortal(
          <DetailsPopup
            onClose={() => setShowWFDetailsModal(false)}
            workflowData={workflowFromState || { workflow_id }}
          />,
          document.body
        )}
    </>
  );
}

export default WorkflowDetails;
